import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  doc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import { dbLOA } from "../../../../backend/firebaseconfig";
import { useUserEmail } from "../../../../context/UserEmailContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import { useUserPermissions } from "../../../../context/UserPermissionsContext";
import { useAdminAdviserMails } from "../../../../context/AdminAdviserEmailsContext";
import { getRelatedEmails } from "../../AML/Utils/getRelatedEmails";

import "../../../../css/LOA/LOADashboard.css";

const PAGE_SIZE = 12;
const FETCH_LIMIT_PER_EMAIL = 100;

const normalise = (value) => (value || "").toString().trim().toLowerCase();

const backPropagateCedingSchemeParents = async (items = []) => {
  const uniqueOwners = Array.from(
    new Set(items.map((item) => normalise(item.ownerEmail)).filter(Boolean)),
  );

  if (!uniqueOwners.length) return;

  await Promise.allSettled(
    uniqueOwners.map((ownerEmail) =>
      setDoc(
        doc(dbLOA, "Ceding Schemes", ownerEmail),
        {
          ownerEmail,
          updatedAt: serverTimestamp(),
          backPropagatedFromDashboard: true,
        },
        { merge: true },
      ),
    ),
  );
};

const canViewAllLOACases = (role, permissions = []) => {
  return (
    role === "Staff" &&
    Array.isArray(permissions) &&
    (permissions.includes("LOA Admin") ||
      permissions.includes("Compliance") ||
      permissions.includes("Compliance Support") ||
      permissions.includes("Superadmin"))
  );
};

const getTimestampValue = (value) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (value?.toDate) {
    return value.toDate().getTime();
  }

  const parsed = new Date(value).getTime();

  return Number.isNaN(parsed) ? 0 : parsed;
};

const getCreatedTime = (item) => {
  return (
    getTimestampValue(item.createdAtMs) ||
    getTimestampValue(item.createdAt) ||
    getTimestampValue(item.updatedAtMs) ||
    getTimestampValue(item.updatedAt) ||
    0
  );
};

const sortDashboardItems = (items = []) => {
  return [...items].sort((a, b) => getCreatedTime(b) - getCreatedTime(a));
};

const getCountsFromAnswers = (answers = []) => {
  return answers.reduce(
    (acc, row) => {
      const status = row?.status || "missing";

      if (status === "found") acc.found += 1;
      else if (status === "partial") acc.partial += 1;
      else acc.missing += 1;

      return acc;
    },
    { found: 0, partial: 0, missing: 0 },
  );
};

const hasRealCounts = (counts) => {
  return (
    counts &&
    ((counts.found || 0) > 0 ||
      (counts.partial || 0) > 0 ||
      (counts.missing || 0) > 0)
  );
};

const getSchemeCounts = (scheme = {}) => {
  const answerCounts = getCountsFromAnswers(
    scheme.extractionJson?.answers || scheme.answers || [],
  );

  if (hasRealCounts(scheme.counts)) {
    return {
      found: scheme.counts.found || 0,
      partial: scheme.counts.partial || 0,
      missing: scheme.counts.missing || 0,
    };
  }

  return answerCounts;
};

const getItemCounts = (item = {}) => {
  const answerCounts = getCountsFromAnswers(
    item.extractionJson?.answers || item.answers || [],
  );

  if (hasRealCounts(item.counts)) {
    return {
      found: item.counts.found || 0,
      partial: item.counts.partial || 0,
      missing: item.counts.missing || 0,
    };
  }

  return answerCounts;
};

const getCountsTotal = (schemes = []) => {
  return schemes.reduce(
    (acc, scheme) => {
      const counts = getSchemeCounts(scheme);

      acc.found += counts.found;
      acc.partial += counts.partial;
      acc.missing += counts.missing;

      return acc;
    },
    { found: 0, partial: 0, missing: 0 },
  );
};

const getFileNamesFromSchemes = (schemes = []) => {
  return schemes
    .flatMap((scheme) => {
      if (Array.isArray(scheme.fileNames) && scheme.fileNames.length > 0) {
        return scheme.fileNames;
      }

      if (Array.isArray(scheme.savedFiles) && scheme.savedFiles.length > 0) {
        return scheme.savedFiles.map((file) => file.fileName || file.name);
      }

      if (Array.isArray(scheme.files) && scheme.files.length > 0) {
        return scheme.files.map((file) => file.fileName || file.name);
      }

      return [];
    })
    .filter(Boolean);
};

const formatCreatedDate = (item) => {
  const time = getCreatedTime(item);

  if (!time) return "Date unavailable";

  return new Date(time).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPrimarySummary = (item) => {
  const status = item.status || "completed";
  const schemes = Array.isArray(item.schemes) ? item.schemes : [];
  const fileNames = getFileNamesFromSchemes(schemes);

  if (status !== "completed") {
    return {
      clientName:
        item.summary?.clientName ||
        (status === "failed" ? "Extraction failed" : "Processing extraction"),

      providerName:
        item.progressMessage ||
        (status === "failed"
          ? "The extraction failed and staff have been notified."
          : "Files received. Extraction is processing."),

      policyNumber: "",
      schemeCount: schemes.length || 1,
      counts: getItemCounts(item),
      status,
      progressMessage: item.progressMessage || "Processing...",
      fileNames,
      error: item.error || "",
    };
  }

  if (schemes.length > 0) {
    const completedSchemes = schemes.filter((scheme) => scheme.extractionJson);
    const firstScheme = completedSchemes[0] || schemes[0];

    return {
      clientName:
        firstScheme.summary?.clientName ||
        firstScheme.extractionJson?.summary?.clientName ||
        item.summary?.clientName ||
        "Client not found",

      providerName:
        completedSchemes
          .map(
            (scheme) =>
              scheme.summary?.providerName ||
              scheme.extractionJson?.summary?.providerName,
          )
          .filter(Boolean)
          .join(", ") ||
        item.summary?.providerName ||
        "Provider not found",

      policyNumber:
        firstScheme.summary?.policyNumber ||
        firstScheme.extractionJson?.summary?.policyNumber ||
        item.summary?.policyNumber ||
        "",

      schemeCount: schemes.length,
      counts: getCountsTotal(schemes),
      status,
      progressMessage: item.progressMessage || "",
      fileNames,
      error: item.error || "",
    };
  }

  return {
    clientName:
      item.summary?.clientName ||
      item.extractionJson?.summary?.clientName ||
      "Client not found",

    providerName:
      item.summary?.providerName ||
      item.extractionJson?.summary?.providerName ||
      "Provider not found",

    policyNumber:
      item.summary?.policyNumber ||
      item.extractionJson?.summary?.policyNumber ||
      "",

    schemeCount: 1,
    counts: getItemCounts(item),
    status,
    progressMessage: item.progressMessage || "",
    fileNames,
    error: item.error || "",
  };
};

const buildSearchText = (item) => {
  const summary = item.dashboardSummary || getPrimarySummary(item);

  return [
    summary.clientName,
    summary.providerName,
    summary.policyNumber,
    summary.status,
    summary.progressMessage,
    summary.error,
    item.ownerEmail,
    item.createdBy,
    item.userEmail,
    item.adviserEmail,
    ...(summary.fileNames || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage, "...", totalPages];
};

const LOADashboard = () => {
  const navigate = useNavigate();

  const breadcrumbItems = [
    { label: "LOA Toolkit", to: "/portal/loa-toolkit" },
    { label: "ProCeding" },
  ];

  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { userPermissions } = useUserPermissions();
  const { adminAdviserMails } = useAdminAdviserMails();

  const [items, setItems] = useState([]);
  const [relatedEmails, setRelatedEmails] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const safeUserEmail = normalise(userEmail);
  const viewAll = canViewAllLOACases(userRole, userPermissions);

  useEffect(() => {
    if (!safeUserEmail || !userRole || viewAll) {
      setRelatedEmails([]);
      return;
    }

    let cancelled = false;

    const fetchRelatedEmails = async () => {
      try {
        const emails = await getRelatedEmails(safeUserEmail, userRole);
        const emailSet = new Set();

        emailSet.add(safeUserEmail);

        emails.forEach((email) => {
          const cleanEmail = normalise(email);
          if (cleanEmail) emailSet.add(cleanEmail);
        });

        if (Array.isArray(adminAdviserMails)) {
          adminAdviserMails.forEach((email) => {
            const cleanEmail = normalise(email);
            if (cleanEmail) emailSet.add(cleanEmail);
          });
        }

        if (!cancelled) {
          setRelatedEmails(Array.from(emailSet));
        }
      } catch (error) {
        console.error("Failed to fetch related LOA emails:", error);

        if (!cancelled) {
          setRelatedEmails([safeUserEmail]);
        }
      }
    };

    fetchRelatedEmails();

    return () => {
      cancelled = true;
    };
  }, [safeUserEmail, userRole, viewAll, adminAdviserMails]);

  useEffect(() => {
    if (!safeUserEmail) return;

    setLoading(true);

    const unsubscribers = [];
    const docsByEmail = {};

    if (viewAll) {
      const q = query(collectionGroup(dbLOA, "Extractions"), limit(300));

      const unsub = onSnapshot(
        q,
        (snapshot) => {
          const mappedItems = snapshot.docs.map((docSnap) => {
            const ownerEmail = normalise(docSnap.ref?.parent?.parent?.id);

            return {
              id: docSnap.id,
              ownerEmail,
              ...docSnap.data(),
            };
          });

          backPropagateCedingSchemeParents(mappedItems).catch((error) => {
            console.error("Failed to back-propagate LOA parent docs:", error);
          });

          setItems(sortDashboardItems(mappedItems));
          setPage(1);
          setLoading(false);
        },
        (error) => {
          console.error("Failed to load all LOA cases:", error);
          setItems([]);
          setLoading(false);
        },
      );

      return () => unsub();
    }

    const emailsToQuery =
      relatedEmails.length > 0 ? relatedEmails : [safeUserEmail];

    console.log("[LOADashboard] Querying Firestore for emails:", emailsToQuery);

    emailsToQuery.forEach((email) => {
      const cleanEmail = normalise(email);
      if (!cleanEmail) return;

      console.log(
        `[LOADashboard] Subscribing to Ceding Schemes/${cleanEmail}/Extractions`,
      );

      const q = query(
        collection(dbLOA, "Ceding Schemes", cleanEmail, "Extractions"),
        limit(FETCH_LIMIT_PER_EMAIL),
      );

      const unsub = onSnapshot(
        q,
        (snapshot) => {
          docsByEmail[cleanEmail] = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ownerEmail: cleanEmail,
            ...docSnap.data(),
          }));

          const mergedItems = sortDashboardItems(
            Object.values(docsByEmail).flat(),
          );

          backPropagateCedingSchemeParents(mergedItems).catch((error) => {
            console.error("Failed to back-propagate LOA parent docs:", error);
          });

          setItems(mergedItems);
          setPage(1);
          setLoading(false);
        },
        (error) => {
          console.error(`Failed to load LOA cases for ${cleanEmail}:`, error);
          setLoading(false);
        },
      );

      unsubscribers.push(unsub);
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub && unsub());
    };
  }, [safeUserEmail, viewAll, relatedEmails]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const dashboardItems = useMemo(() => {
    return sortDashboardItems(items).map((item) => ({
      ...item,
      dashboardSummary: getPrimarySummary(item),
    }));
  }, [items]);

  const filteredItems = useMemo(() => {
    const cleanSearch = normalise(searchTerm);

    if (!cleanSearch) return dashboardItems;

    return dashboardItems.filter((item) =>
      buildSearchText(item).includes(cleanSearch),
    );
  }, [dashboardItems, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));

const paginatedItems = useMemo(() => {
  const start = (page - 1) * PAGE_SIZE;
  return filteredItems.slice(start, start + PAGE_SIZE);
}, [filteredItems, page]);

const paginationItems = useMemo(
  () => getPaginationItems(page, totalPages),
  [page, totalPages],
);

  if (!safeUserEmail) {
    return (
      <div className="loa-content">
        <Sidebar />

        <main className="loa-main">
          <div className="loa-dashboard">
            <div className="loa-empty-card">
              Sign in to load your ceding scheme cases.
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="loa-content">
      <Helmet>
        <title>ProCeding Dashboard</title>
        <meta
          name="description"
          content="Rosemount Financial Solutions (IFA) Ltd - Adviser Portal"
        />
      </Helmet>

      <Sidebar />

      <main className="loa-main">
        <div className="loa-dashboard">
          <Breadcrumb items={breadcrumbItems} />

          <div className="loa-dashboard-header">
            <div>
              <h1>ProCeding Dashboard</h1>
              <p>
                {viewAll
                  ? "View all saved ProCeding extractions."
                  : "View your saved and related ProCeding extractions."}
              </p>
            </div>

            <button
              type="button"
              className="loa-primary-btn"
              onClick={() => navigate("/pro-ceding/new")}
            >
              + New Ceding Scheme
            </button>
          </div>

          <div className="loa-dashboard-toolbar">
            <div className="loa-dashboard-search">
              <input
                type="text"
                value={searchTerm}
                placeholder="Search client, provider, policy, file, adviser or status..."
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {searchTerm && (
                <button type="button" onClick={() => setSearchTerm("")}>
                  Clear
                </button>
              )}
            </div>

            <p className="loa-dashboard-result-count">
              Showing {filteredItems.length} of {dashboardItems.length}
            </p>
          </div>

          <div className="loa-dashboard-grid">
            {loading && paginatedItems.length === 0 && (
              <div className="loa-empty-card">Loading ceding schemes…</div>
            )}

            {!loading &&
              paginatedItems.map((item) => {
                const ownerEmail =
                  normalise(item.ownerEmail) ||
                  normalise(item.adviserEmail) ||
                  normalise(item.userEmail) ||
                  normalise(item.createdBy) ||
                  safeUserEmail;

                const summary = item.dashboardSummary;

                return (
                  <div className="loa-dashboard-card" key={item.id}>
                    <div className="loa-dashboard-card-top">
                      <div>
                        <div className="loa-dashboard-actions">
                          <h2>{summary.clientName}</h2>

                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/pro-ceding/new?extractionId=${item.id}&ownerEmail=${encodeURIComponent(
                                  ownerEmail,
                                )}`,
                              )
                            }
                          >
                            View Details
                          </button>
                        </div>

                        <p>
                          {summary.providerName}
                          {summary.policyNumber
                            ? ` · ${summary.policyNumber}`
                            : ""}
                        </p>

                        <p className="loa-dashboard-date">
                          {formatCreatedDate(item)}
                        </p>
                      </div>
                    </div>

                    {(viewAll || ownerEmail !== safeUserEmail) && (
                      <p className="loa-dashboard-user">
                        Created by: {item.createdBy || ownerEmail || "Unknown"}
                      </p>
                    )}

                    {summary.status !== "completed" ? (
                      <div className="loa-dashboard-processing-block">
                        <div className="loa-dashboard-counts">
                          <span
                            className={
                              summary.status === "failed"
                                ? "missing"
                                : "processing"
                            }
                          >
                            {summary.status === "failed"
                              ? "Failed"
                              : "Processing"}
                          </span>
                        </div>

                        {summary.status === "failed" && summary.error && (
                          <p className="loa-dashboard-error">{summary.error}</p>
                        )}
                      </div>
                    ) : (
                      <div className="loa-dashboard-counts">
                        <span className="found">
                          Found: {summary.counts.found || 0}
                        </span>

                        <span className="partial">
                          Partial: {summary.counts.partial || 0}
                        </span>

                        <span className="missing">
                          Missing: {summary.counts.missing || 0}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

            {!loading && paginatedItems.length === 0 && (
              <div className="loa-empty-card">
                {searchTerm
                  ? "No ceding scheme extractions match your search."
                  : "No ceding scheme extractions saved yet."}
              </div>
            )}
          </div>

          {filteredItems.length > PAGE_SIZE && (
            <div className="loa-pagination">
              <button
                type="button"
                className="loa-page-text"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page === 1}
              >
                Prev
              </button>

              {paginationItems.map((item, index) =>
                item === "..." ? (
                  <span key={`ellipsis-${index}`} className="loa-page-ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`loa-page-circle ${page === item ? "active" : ""}`}
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </button>
                ),
              )}

              <button
                type="button"
                className="loa-page-text"
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LOADashboard;
